/**
 * employerAuthority.js — single source of truth for FE authority
 * derivation (Prompt 3 Part A + B).
 *
 * Maps `emp_account_members.role` → boolean predicates. Mirrors the
 * post-patch backend enforcement of the Phase 0.7 + Phase 1.0
 * surfaces. If the backend RPC predicates change, update THIS file
 * to keep the help-page matrix and the FE gates in lockstep.
 *
 * Authority classes
 * ─────────────────
 *   A0 = owner-only
 *   A1 = admin OR owner            (member governance writes)
 *   A2 = recruiter OR admin OR owner (role + reviewer lifecycle writes)
 *   A3 = reviewer-only (self-reviews; not gated here)
 *
 * Owner is admin-equivalent for every employer write. Recruiter is
 * an operational role for roles + reviewer lifecycle but cannot
 * touch member governance.
 */

export const EMPLOYER_ROLES = ['owner', 'admin', 'recruiter', 'reviewer'];

export const isOwner     = (r) => r === 'owner';
export const isAdmin     = (r) => r === 'admin';
export const isRecruiter = (r) => r === 'recruiter';
export const isReviewer  = (r) => r === 'reviewer';

/** A1 — owner OR admin. Member governance writes. */
export const hasA1 = (r) => r === 'owner' || r === 'admin';

/** A2 — owner OR admin OR recruiter. Role + reviewer lifecycle. */
export const hasA2 = (r) =>
  r === 'owner' || r === 'admin' || r === 'recruiter';

/** Convenience: can this caller manage the org profile? (A1) */
export const canManageOrg = hasA1;

/** Convenience: can this caller manage members + invitations? (A1) */
export const canManageMembers = hasA1;

/** Convenience: can this caller create / publish / close roles? (A2) */
export const canManageRoles = hasA2;

/** Convenience: can this caller assign / route reviewers? (A2) */
export const canManageReviewers = hasA2;

/** Convenience: can this caller take review decisions? (A3 self-only) */
export const canTakeReviewDecisions = isReviewer;

/** Localised role labels for FE display. */
export const ROLE_LABEL = {
  owner:     'Owner',
  admin:     'Admin',
  recruiter: 'Recruiter',
  reviewer:  'Reviewer',
};

/** One-line, operationally honest description per role. */
export const ROLE_DESCRIPTION = {
  owner:
    'Created the organisation. Highest authority. Can do everything an admin can do, and is the only role auto-assigned at organisation creation.',
  admin:
    'Account governance. Invites members, modifies roles, suspends, revokes, and shares full reviewer-lifecycle controls with recruiters.',
  recruiter:
    'Operational hiring authority. Creates and publishes roles, runs matching, sends interest signals, assigns reviewers, and routes candidates. Cannot manage members.',
  reviewer:
    'Reviews candidates routed to them. Can start, decide (advance / reject / on-hold), and add append-only notes on their own reviews only. No org or member management.',
};

/** Permissions matrix — every cell reflects POST-PATCH enforcement
 *  (after migration 100 is applied). Each entry is one of:
 *    'yes' | 'self' | 'no'
 *  `self` means "only on rows owned by the caller" (e.g. reviewer
 *  decisions are scoped to the caller's own review rows).
 */
export const PERMISSIONS_MATRIX = [
  /* ── Organisation ──────────────────────────────────────────── */
  { area: 'Organisation', action: 'Edit organisation profile',
    owner: 'yes', admin: 'yes', recruiter: 'no', reviewer: 'no' },
  { area: 'Organisation', action: 'View plan & usage',
    owner: 'yes', admin: 'yes', recruiter: 'yes', reviewer: 'yes' },

  /* ── Members ──────────────────────────────────────────────── */
  { area: 'Members', action: 'Invite a new member',
    owner: 'yes', admin: 'yes', recruiter: 'no', reviewer: 'no' },
  { area: 'Members', action: 'Reissue / revoke pending invitation',
    owner: 'yes', admin: 'yes', recruiter: 'no', reviewer: 'no' },
  { area: 'Members', action: 'Change a member’s role',
    owner: 'yes', admin: 'yes', recruiter: 'no', reviewer: 'no' },
  { area: 'Members', action: 'Suspend / unsuspend a member',
    owner: 'yes', admin: 'yes', recruiter: 'no', reviewer: 'no' },
  { area: 'Members', action: 'Revoke a member',
    owner: 'yes', admin: 'yes', recruiter: 'no', reviewer: 'no' },

  /* ── Roles ────────────────────────────────────────────────── */
  { area: 'Roles', action: 'Create / edit a role draft',
    owner: 'yes', admin: 'yes', recruiter: 'yes', reviewer: 'no' },
  { area: 'Roles', action: 'Publish / close / archive a role',
    owner: 'yes', admin: 'yes', recruiter: 'yes', reviewer: 'no' },
  /* RC 1.62 Publish-Snapshot Doctrine — NDOR-Surfaced Candidates
     are generated automatically ONCE on publish. There is no
     manual matching capability for employer roles; the
     operator-only legacy page (`/employer/roles/:id/matches`)
     is for debug/forensic re-runs only and is gated behind
     `OperatorOnlyRoute`. The row is intentionally omitted from
     the user-facing authority matrix. */
  { area: 'Roles', action: 'Send an interest signal',
    owner: 'yes', admin: 'yes', recruiter: 'yes', reviewer: 'no' },

  /* ── Reviewers ─────────────────────────────────────────────── */
  { area: 'Reviewers', action: 'Assign / pause / resume / unassign a reviewer',
    owner: 'yes', admin: 'yes', recruiter: 'yes', reviewer: 'no' },
  { area: 'Reviewers', action: 'Route a revealed candidate to reviewers',
    owner: 'yes', admin: 'yes', recruiter: 'yes', reviewer: 'no' },

  /* ── Reviews ───────────────────────────────────────────────── */
  { area: 'Reviews', action: 'Start a review',
    owner: 'no', admin: 'no', recruiter: 'no', reviewer: 'self' },
  { area: 'Reviews', action: 'Decide (advance / reject / on-hold)',
    owner: 'no', admin: 'no', recruiter: 'no', reviewer: 'self' },
  { area: 'Reviews', action: 'Add a review note',
    owner: 'no', admin: 'no', recruiter: 'no', reviewer: 'self' },

  /* ── Audit ─────────────────────────────────────────────────── */
  { area: 'Audit', action: 'View account-wide activity log',
    owner: 'yes', admin: 'yes', recruiter: 'yes', reviewer: 'self' },
];
